<template>
  <div class="app">
    <Snackbar :snackbar="snackbar" :text="text" @change="snackbar = $event" />
    <v-sheet v-show="showSkeleton" class="pa-3">
      <v-row>
        <v-col v-for="item in 4" :key="item">
          <v-skeleton-loader class="mx-auto" type="list-item" />
        </v-col>
      </v-row>
      <v-row>
        <v-col v-for="item in 4" :key="item">
          <v-skeleton-loader class="mx-auto" type="list-item" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-skeleton-loader class="mx-auto" type="button" />
        </v-col>
      </v-row>
      <v-row>
        <v-col v-for="item in 2" :key="item">
          <v-skeleton-loader class="mx-auto" type="image" height="220" />
        </v-col>
      </v-row>
    </v-sheet>
    <v-form ref="form" v-show="!showSkeleton">
      <v-row>
        <v-col cols="3">
          <v-text-field
            dense
            :value="form.description.toUpperCase()"
            :error-messages="descriptionErrors"
            :counter="30"
            label="Nome"
            outlined
            @input="
              $v.form.description.$touch()
              form.description = $event.toUpperCase().trim()
            "
            @blur="$v.form.description.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            dense
            v-model="form.ipServidor"
            :error-messages="ipServidorErrors"
            label="Endereço de Registro"
            outlined
            @input="
              $v.form.ipServidor.$touch()
              form.ipServidor = $event.trim()
            "
            @blur="$v.form.ipServidor.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            dense
            v-model="form.token"
            :error-messages="tokenErrors"
            label="Token"
            outlined
            @input="
              $v.form.token.$touch()
              form.token = $event.trim()
            "
            @blur="$v.form.token.$touch()"
            :type="show ? 'text' : 'password'"
            @click:append="show = !show"
            :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            dense
            v-model="form.porta"
            :error-messages="portaErrors"
            label="Porta"
            type="number"
            outlined
            @input="$v.form.porta.$touch()"
            @blur="$v.form.porta.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <v-select
            dense
            v-model="form.nat"
            :error-messages="natErrors"
            :items="nat"
            outlined
            label="NAT"
            @input="$v.form.nat.$touch()"
            @blur="$v.form.nat.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            dense
            v-model="form.dtmf"
            :error-messages="dtmfErrors"
            :items="dtmf"
            outlined
            label="DTMF"
            @input="$v.form.dtmf.$touch()"
            @blur="$v.form.dtmf.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            dense
            v-model="form.quality"
            :error-messages="qualityErrors"
            :items="quality"
            outlined
            label="Quality"
            @input="$v.form.quality.$touch()"
            @blur="$v.form.quality.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            dense
            v-model="form.insecure"
            :error-messages="insecureErrors"
            :items="insecure"
            outlined
            multiple
            label="Insecure"
            @input="$v.form.insecure.$touch()"
            @blur="$v.form.insecure.$touch()"
          />
        </v-col>
      </v-row>
      <v-checkbox dense v-model="form.interno" :label="`Interno`" />
      <v-row>
        <v-col>
          <DraggAndDrop
            dragname="Codecs"
            :valoresDisponiveisComboBox="codecs.codecsDisponiveis"
            @dragUpdate="updateCodecs($event)"
            :opcoes-selecionadas="codecs.codecsSelecionadas"
          />
        </v-col>
        <v-col v-show="!form.interno">
          <DraggAndDrop
            dragname="Rotas"
            :valoresDisponiveisComboBox="rotasGerais.rotasGeralDisponiveis"
            @dragUpdate="updateRotasGeral($event)"
            :opcoesSelecionadas="rotasGerais.rotasGeralSelecionadas"
          />
        </v-col>
      </v-row>
      <v-divider dark vertical />
      <v-divider dark />
      <v-divider dark vertical />
      <FixedButtons @salvar="salvar" @cancelar="cancelar" :pararLoader="pararLoader" />
    </v-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import Snackbar from '@/componentes/Snackbar'
import FixedButtons from '@/componentes/FixedButtons'
import { registerVuexModule, formatarRota } from '@/utils/vuexUtils'
import { ordenar } from '@/utils/ordenarArrayByText'
import DraggAndDrop from '../_components/DraggAndDrop'
import troncosStore from '../_store'
import { validateData, validateComputed } from '../_validates/validateIaxtrunk'
import TroncosHttpService from '../_services/TroncosHttpService'

export default {
  name: 'AbaTroncoIaxTrunk',
  props: {
    edicao: String,
    _id: String,
  },
  components: { DraggAndDrop, Snackbar, FixedButtons },
  mixins: [validationMixin],
  data: () => ({
    form: {
      description: '',
      tipo: 'IAXTRUNK',
      ipServidor: '',
      token: '',
      porta: '4569',
      codecs: [],
      nat: 'yes',
      dtmf: 'rfc2833',
      quality: 'yes',
      interno: false,
      insecure: ['INVITE'],
      rota: [],
    },
    rotasGerais: {
      rotasGeralDisponiveis: [],
      rotasGeralSelecionadas: [],
    },
    codecs: {
      codecsDisponiveis: ['alaw', 'ulaw', 'gsm', 'g729', 'h263', 'h264'],
      codecsSelecionadas: [],
    },
    dtmf: ['rfc2833', 'inband', 'info'],
    quality: ['yes', 'no'],
    nat: ['yes', 'no'],
    insecure: ['INVITE', 'PORT'],
    show: false,
    text: '',
    snackbar: false,
    showSkeleton: true,
    pararLoader: {
      parar: false,
    },
  }),

  validations: {
    ...validateData,
  },

  computed: {
    ...mapGetters('abaTroncoIaxTrunkModulePABXStore', ['getRotasDisponiveis', 'getTroncoSelecionado']),
    ...validateComputed,
  },

  async created() {
    registerVuexModule(this.$store, 'abaTroncoIaxTrunkModulePABXStore', troncosStore)

    await this.listarRotas()

    this.rotasGerais.rotasGeralDisponiveis = [...this.getRotasDisponiveis]

    if (this.edicao === 'true') {
      this.form = this.getTroncoSelecionado

      const arrayrotasSelecionadas = Object.values(this.getTroncoSelecionado.rota)

      arrayrotasSelecionadas.forEach(rotasSelecionadas => {
        const indexRotaGeral = this.rotasGerais.rotasGeralDisponiveis.findIndex(
          rotaGeral => rotaGeral._id === rotasSelecionadas
        )

        if (indexRotaGeral !== -1) {
          this.rotasGerais.rotasGeralSelecionadas.push(this.rotasGerais.rotasGeralDisponiveis[indexRotaGeral])
        }
      })

      if (typeof this.form.codecs !== 'undefined') {
        this.codecs.codecsSelecionadas = [...this.form.codecs]
      }
    } else {
      this.codecs.codecsSelecionadas.push('alaw', 'ulaw', 'gsm')
      this.rotasGerais.rotasGeralSelecionadas = this.getRotasDisponiveis.filter(rota => rota.default)
    }

    ordenar(this.rotasGerais.rotasGeralDisponiveis)
    ordenar(this.codecs.codecsDisponiveis)
    this.showSkeleton = false
  },

  methods: {
    ...mapActions('abaTroncoIaxTrunkModulePABXStore', ['listarRotas']),
    updateCodecs(event) {
      this.form.codecs = event
    },
    updateRotasGeral(event) {
      this.form.rota = formatarRota(event)
    },
    async salvar() {
      this.pararLoader.parar = false

      if (!this.form.codecs.length) {
        this.text = 'Nenhum codec Selecionado'
        this.snackbar = true
        this.pararLoader.parar = true
        return
      } else if (!this.form.rota['1']) {
        this.text = 'Nenhuma rota selecionada.'
        this.snackbar = true
        this.pararLoader.parar = true
        return
      }

      this.$v.$touch()
      if (this.$v.$error) {
        this.$vuetify.goTo(1, { duration: 1000, easing: 'easeOutCubic' })
        this.pararLoader.parar = true
        return
      }
      try {
        if (this.edicao === 'true') {
          delete this.form.numero
          await TroncosHttpService.putTronco(this.form)
        } else {
          await TroncosHttpService.postTronco(this.form)
        }
        this.$router.push({ name: 'Troncos' })
      } catch (error) {
        this.text = error.response.data
        this.snackbar = true
        this.pararLoader.parar = true
      }
    },
    cancelar() {
      this.$router.push({ name: 'Troncos' })
    },
  },
}
</script>

<style scoped>
.app {
  background-color: var(--v-background-base);
}
</style>
